import type { LocaleBundle, SupportedLocale, UseI18nReturn } from '@/types';
import enBundle from '@/locales/en.json';
import jaBundle from '@/locales/ja.json';
import { resolveLocale } from '@/utils/locale';
import { useCallback } from 'react';

/**
 * Locale bundles are imported statically (eagerly bundled) rather than lazily
 * fetched. With only two small locales, bundling them removes a second network
 * round-trip on language switch — the previous dynamic `import()` meant the
 * chosen language could fail to load (slow network / offline), silently falling
 * back to English. Static import guarantees the language is available
 * synchronously, so the menu never renders the wrong language while a chunk
 * downloads.
 */
const BUNDLES: Record<SupportedLocale, LocaleBundle> = {
  en: enBundle as LocaleBundle,
  ja: jaBundle as LocaleBundle,
};

function getLocaleBundle(locale: SupportedLocale): LocaleBundle {
  return BUNDLES[locale] ?? BUNDLES.en;
}

/**
 * React hook that returns the translation function for the resolved language.
 *
 * - Bundles are statically imported, so translations are available
 *   synchronously — `isLoading` is always `false` and there is no key flash.
 * - Unsupported locales resolve to English.
 * - Switches language without remount when `lang` changes.
 */
export function useI18n(lang: string): UseI18nReturn {
  const resolvedLocale = resolveLocale(lang);
  const bundle = getLocaleBundle(resolvedLocale);

  // Bundles are eagerly available, so there is no async load — the correct
  // language is ready on the very first render and never lags behind.
  const isLoading = false;

  const t = useCallback(
    (key: string): string => (bundle as unknown as Record<string, string>)[key] ?? key,
    [bundle],
  );

  return { t, isLoading, currentLocale: resolvedLocale };
}
